import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "users-charts" }
const _hoisted_2 = { class: "row g-3" }
const _hoisted_3 = { class: "col-12 col-lg-6" }
const _hoisted_4 = { class: "col-12 col-lg-6" }
const _hoisted_5 = {
  class: "btn-group btn-group-sm me-2",
  role: "group",
  "aria-label": "Ranglisten-Auswahl"
}
const _hoisted_6 = ["onClick"]

import { computed, reactive, shallowRef } from 'vue';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import ChartCard from './ChartCard.vue';
import RankingChart from './RankingChart.vue';


export default {
  __name: 'UsersCharts',
  props: {
  analytics: {
    type: Object,
    default: null,
  },
  bookingsLoading: {
    type: Boolean,
    default: false,
  },
  analyticsError: {
    type: [Object, Error, String],
    default: null,
  },
  rangeDays: {
    type: Number,
    default: 30,
  },
},
  setup(__props) {

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
]);

const props = __props;

const activeRankTab = shallowRef('users');
const rankLimits = reactive({ users: 10, foreign: 10, rooms: 10 });

const formatDate = (dateString) => {
  const date = parseISO(dateString);
  return isValid(date) ? format(date, 'EEEEEE dd.MM', { locale: de }) : 'Invalid Date';
};

const dailyTrendData = computed(() => {
  const trend = Array.isArray(props.analytics?.dailyTrend)
    ? props.analytics.dailyTrend
    : [];

  return trend.map((item) => ({
    date: item.date,
    label: formatDate(item.date),
    count: item.count || 0,
  }));
});

const dailyTrendChartOption = computed(() => {
  const counts = dailyTrendData.value.map((item) => item.count);
  const labels = dailyTrendData.value.map((item) => item.label);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
    },
    grid: { left: '4%', right: '3%', top: 16, bottom: 32, containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        interval: props.rangeDays > 14 ? 4 : props.rangeDays > 7 ? 1 : 0,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
    },
    series: [
      {
        name: 'Buchungen (Tag)',
        type: 'bar',
        data: counts,
        barMaxWidth: 16,
        itemStyle: {
          color: '#cbd5e1',
          borderRadius: [2, 2, 0, 0],
        },
        emphasis: {
          itemStyle: { color: '#94a3b8' },
        },
        animationDuration: 550,
        animationEasing: 'cubicOut',
      },
      {
        name: 'Verlauf',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: counts,
        lineStyle: {
          color: '#0284c7',
          width: 2.2,
        },
        areaStyle: {
          color: 'rgba(2, 132, 199, 0.06)',
        },
        animationDuration: 650,
        animationDelay: 550,
        animationEasing: 'cubicOut',
      },
    ],
  };
});

/** Full sorted ranking (limit applied inside RankingChart). */
const topUsersChartRows = computed(() => {
  const students = (props.analytics?.topUsers?.students || [])
    .map((item) => ({
      label: `S: ${item.label}`,
      count: item.count,
      role: 'student',
    }));

  const employees = (props.analytics?.topUsers?.employees || [])
    .map((item) => ({
      label: `M: ${item.label}`,
      count: item.count,
      role: 'employee',
    }));

  return [...students, ...employees].sort((a, b) => b.count - a.count);
});

/** Full sorted ranking (limit applied inside RankingChart). */
const topForeignChartRows = computed(() => {
  if (!Array.isArray(props.analytics?.topForeignBookers)) {
    return [];
  }

  return props.analytics.topForeignBookers
    .map((item) => ({
      label: item.label,
      count: item.count || 0,
    }))
    .sort((a, b) => b.count - a.count);
});

/** Full sorted ranking (limit applied inside RankingChart). */
const topRoomsChartRows = computed(() => {
  if (!Array.isArray(props.analytics?.topRooms)) {
    return [];
  }

  return props.analytics.topRooms
    .map((item) => ({
      label: item.label,
      count: item.count || 0,
    }))
    .sort((a, b) => b.count - a.count);
});

const rankTabs = [
  { key: 'users', label: 'Studenten' },
  { key: 'foreign', label: 'Fremdbucher' },
  { key: 'rooms', label: 'Räume' },
];

const activeRanking = computed(() => {
  switch (activeRankTab.value) {
    case 'foreign':
      return { title: 'Top Fremdbucher', rows: topForeignChartRows.value, colorByRole: false, barColor: '#ee6666' };
    case 'rooms':
      return { title: 'Top Räume', rows: topRoomsChartRows.value, colorByRole: false, barColor: '#f59e0b' };
    default:
      return { title: 'Top Nutzer', rows: topUsersChartRows.value, colorByRole: true, barColor: '#ee6666' };
  }
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(ChartCard, {
          title: "Buchungen pro Tag",
          loading: __props.bookingsLoading,
          error: __props.analyticsError,
          "is-empty": dailyTrendData.value.length === 0
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(VChart), {
              class: "chart",
              option: dailyTrendChartOption.value,
              autoresize: ""
            }, null, 8 /* PROPS */, ["option"])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["loading", "error", "is-empty"])
      ]),
      _createCommentVNode(" Right Deck: Unified Toggleable Rankings "),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(RankingChart, {
          limit: rankLimits[activeRankTab.value],
          "onUpdate:limit": _cache[0] || (_cache[0] = $event => ((rankLimits[activeRankTab.value]) = $event)),
          title: activeRanking.value.title,
          "limit-select-id": `top-${activeRankTab.value}-limit`,
          rows: activeRanking.value.rows,
          loading: __props.bookingsLoading,
          error: __props.analyticsError,
          "color-by-role": activeRanking.value.colorByRole,
          "bar-color": activeRanking.value.barColor
        }, {
          "prepend-actions": _withCtx(() => [
            _createElementVNode("div", _hoisted_5, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(rankTabs, (tab) => {
                return _createElementVNode("button", {
                  key: tab.key,
                  type: "button",
                  class: _normalizeClass(["btn btn-sm", activeRankTab.value === tab.key ? 'btn-primary' : 'btn-outline-secondary']),
                  onClick: $event => (activeRankTab.value = tab.key)
                }, _toDisplayString(tab.label), 11 /* TEXT, CLASS, PROPS */, _hoisted_6)
              }), 64 /* STABLE_FRAGMENT */))
            ])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["limit", "title", "limit-select-id", "rows", "loading", "error", "color-by-role", "bar-color"])
      ])
    ])
  ]))
}
}

}